import {
  type FishShopV2Action,
  type FishShopV2State,
  type FishShopV2ViewerState,
  filterStateForViewer,
  reduceGameState,
  validateAction,
} from "@cloudnest/fish-shop-v2-engine";
import {
  type FishShopReplayEvent,
  toFishShopReplayEvent,
} from "./event-adapter";
import type { FishShopReplayFixtureEvent } from "./replay-fixtures.generated";

export interface FishShopReplaySnapshot {
  readonly index: number;
  readonly event: FishShopReplayFixtureEvent;
  readonly replayEvent: FishShopReplayEvent;
  readonly state: FishShopV2State;
  readonly viewerState: FishShopV2ViewerState;
}

export interface BuildFishShopReplayOptions {
  readonly observerUserId?: string;
}

function requireState(
  state: FishShopV2State | null,
  eventType: FishShopReplayEvent["eventType"],
): FishShopV2State {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function toAction(event: FishShopReplayEvent): FishShopV2Action | null {
  switch (event.eventType) {
    case "init":
    case "SyncViewerState":
      return null;
    case "AllocateTickets":
      return {
        type: "AllocateTickets",
        playerId: event.userId,
        ticketsByProduct: event.payload.ticketsByProduct,
      };
    case "SubmitPrices":
      return {
        type: "SubmitPrices",
        playerId: event.userId,
        prices: event.payload.prices,
      };
    case "UseSecret":
      return {
        type: "UseSecret",
        playerId: event.userId,
        productId: event.payload.productId,
      };
    case "UseCheck":
      return { type: "UseCheck", playerId: event.userId };
    case "ConfirmRoundResult":
      return { type: "ConfirmRoundResult", playerId: event.userId };
    case "RestartGame":
      return { type: "RestartGame", playerId: event.userId };
  }
}

function getEligiblePriceSubmitterIds(
  state: FishShopV2State,
): readonly string[] {
  const ids = new Set<string>();
  for (const market of Object.values(state.currentRound.market)) {
    for (const seller of market.sellers) {
      ids.add(seller.playerId);
    }
  }
  return [...ids];
}

function allPlayersAllocated(state: FishShopV2State): boolean {
  return state.players.every((player) =>
    Boolean(state.currentRound.allocations[player.playerId]),
  );
}

function allEligibleSellersSubmitted(state: FishShopV2State): boolean {
  return getEligiblePriceSubmitterIds(state).every((playerId) =>
    Boolean(state.currentRound.priceSubmissions[playerId]),
  );
}

function applyHostAction(
  state: FishShopV2State,
  action: FishShopV2Action,
): FishShopV2State {
  const validation = validateAction(state, action);
  if (!validation.valid) {
    throw new Error(
      `${action.type} rejected: ${validation.error ?? "Validation failed"}`,
    );
  }
  return reduceGameState(state, action);
}

function autoAdvanceAfterAction(
  state: FishShopV2State,
  action: FishShopV2Action,
): FishShopV2State {
  let nextState = state;

  if (
    action.type === "AllocateTickets" &&
    nextState.currentRound.phase === "TICKET_ALLOCATION" &&
    allPlayersAllocated(nextState)
  ) {
    nextState = applyHostAction(nextState, {
      type: "LockMarket",
      playerId: nextState.hostPlayerId,
    });
  }

  if (
    (action.type === "AllocateTickets" || action.type === "SubmitPrices") &&
    (nextState.currentRound.phase === "MARKET_REVEALED" ||
      nextState.currentRound.phase === "PRICE_SUBMISSION") &&
    allEligibleSellersSubmitted(nextState)
  ) {
    nextState = applyHostAction(nextState, {
      type: "RevealRound",
      playerId: nextState.hostPlayerId,
    });
  }

  return nextState;
}

function applyReplayEvent(
  state: FishShopV2State | null,
  event: FishShopReplayEvent,
): FishShopV2State {
  if (event.eventType === "init") {
    return event.payload.state;
  }

  const current = requireState(state, event.eventType);
  if (event.eventType === "SyncViewerState") {
    return current;
  }

  const action = toAction(event);
  if (action === null) {
    return current;
  }

  const validation = validateAction(current, action);
  if (!validation.valid) {
    throw new Error(
      `${event.eventType} rejected: ${validation.error ?? "Validation failed"}`,
    );
  }

  return autoAdvanceAfterAction(reduceGameState(current, action), action);
}

export function buildFishShopReplaySnapshots(
  events: readonly FishShopReplayFixtureEvent[],
  options: BuildFishShopReplayOptions = {},
): readonly FishShopReplaySnapshot[] {
  const observerUserId = options.observerUserId ?? "observer";
  const snapshots: FishShopReplaySnapshot[] = [];
  let state: FishShopV2State | null = null;

  for (const event of events) {
    const replayEvent = toFishShopReplayEvent(event);
    state = applyReplayEvent(state, replayEvent);
    snapshots.push({
      index: snapshots.length,
      event,
      replayEvent,
      state,
      viewerState: filterStateForViewer(state, observerUserId),
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty Fish Shop event log");
  }

  return snapshots;
}
